import { type AppConfiguration, Currency, type Project } from "src/types";
import { getValOrThrowError } from "./fn";

/**
 * Format parts types as specified in https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/formatToParts#description
 */
export enum FormatPartsType {
    currency = "currency",
    decimal = "decimal",
    fraction = "fraction",
    group = "group",
    infinity = "infinity",
    integer = "integer",
    literal = "literal",
    minusSign = "minusSign",
    nan = "nan",
    plusSign = "plusSign",
    percentSign = "percentSign",
    unit = "unit",
}

export type NumberSeparators = Readonly<{ decimalSeparator: string; thousandSeparator: string }>;

const separatorCache: Record<string, NumberSeparators> = {};

const getFormatPartValueByType = (parts: ReadonlyArray<Intl.NumberFormatPart>, type: FormatPartsType): string => {
    const match = parts.find((part) => part.type === type);
    if (match) {
        return match.value;
    }
    throw new Error(`Format parts does not contain type ${type}`);
};

export const getNumberSeparators = (locale: string): NumberSeparators => {
    if (!separatorCache[locale]) {
        const parts = new Intl.NumberFormat(locale).formatToParts(1000.666); // This could be any number as long as its formatted result contains both group separator and decimal separator.
        separatorCache[locale] = {
            decimalSeparator: getFormatPartValueByType(parts, FormatPartsType.decimal),
            thousandSeparator: getFormatPartValueByType(parts, FormatPartsType.group),
        };
    }
    return separatorCache[locale];
};

export const convertPriceInCentsToTargetCurrencyWithCentMultiply = (
    priceInCents: number,
    priceCurrency: Currency,
    targetCurrency: Currency,
    appConfiguration: AppConfiguration | undefined,
): number => {
    // We are multiplying price in BE by 100 to avoid floating point arithmetic issues.
    const price = multiplyPriceInCents(priceInCents);
    return convertPriceToTargetCurrency(price, priceCurrency, targetCurrency, appConfiguration);
};

export const convertPriceToTargetCurrency = (
    price: number,
    priceCurrency: Currency,
    targetCurrency: Currency,
    appConfiguration: AppConfiguration | undefined,
): number => {
    if (priceCurrency === targetCurrency) {
        return price;
    }

    const conversionRateEurToCzk = getValOrThrowError(appConfiguration?.conversionRateEurToCzk, "App config not loaded yet");
    const conversionRateEurToPln = getValOrThrowError(appConfiguration?.conversionRateEurToPln, "App config not loaded yet");
    const conversionTable = createConversionTable(conversionRateEurToCzk, conversionRateEurToPln);
    return convertCurrency(price, priceCurrency, targetCurrency, conversionTable);
};

const createConversionTable = (conversionRateEurToCzk: number, conversionRateEurToPln: number): Map<String, (price: number) => number> => {
    const conversionTable = new Map();

    conversionTable.set(`EUR_CZK`, (amount: number): number => amount * conversionRateEurToCzk);
    conversionTable.set(`CZK_EUR`, (amount: number): number => amount / conversionRateEurToCzk);
    conversionTable.set(`EUR_PLN`, (amount: number): number => amount * conversionRateEurToPln);
    conversionTable.set(`PLN_EUR`, (amount: number): number => amount / conversionRateEurToPln);

    return conversionTable;
};

const convertCurrency = (
    price: number,
    currency: Currency,
    targetCurrency: Currency,
    conversionTable: Map<String, (price: number) => number>,
) => {
    const conversionKey = `${currency}_${targetCurrency}`;
    const conversionFn = conversionTable.get(conversionKey);

    if (!conversionFn) {
        const toEur = conversionTable.get(`${currency}_EUR`);
        const fromEur = conversionTable.get(`EUR_${targetCurrency}`);

        if (!toEur || !fromEur) {
            throw new Error(`Conversion from ${currency} to ${targetCurrency} is not supported.`);
        }

        const inEur = toEur(price);
        return fromEur(inEur);
    }

    return conversionFn(price);
};

export const getCurrentNominalPrice = (tokenCurrentNominalPrice: Project["tokenCurrentNominalPrice"]): number | undefined => {
    if (!tokenCurrentNominalPrice || tokenCurrentNominalPrice.length === 0) {
        return undefined;
    }
    const sortedTokenCurrentNominalPrice = [...tokenCurrentNominalPrice].sort(
        (a, b) => new Date(a.appliedAt!).getTime() - new Date(b.appliedAt!).getTime(),
    );
    const today = new Date();
    return sortedTokenCurrentNominalPrice.reduce((acc, current) => {
        const appliedAt = new Date(current.appliedAt!);
        if (appliedAt > today) {
            return acc;
        }
        return current;
    }, sortedTokenCurrentNominalPrice[0]).price;
};

export const multiplyPriceInCents = (priceInCents?: number): number => (priceInCents ? priceInCents / 100 : 0);
